// 招待リンク (/join/<token>) のプレビュー。
// RPC `get_invite_preview` が返す JSON を防御的にパースする。
// プレビューは「あれば嬉しい」情報であり、参加フローを止めてはいけないため、
// 形が崩れていても例外を投げず null / 空配列に落とす。

export interface InvitePreviewNotebook {
  name: string
  openTaskCount: number
  sampleTasks: string[]
}

export interface InvitePreview {
  workspaceName: string
  memberCount: number
  notebooks: InvitePreviewNotebook[]
}

// RPC 側の上限と揃える (ここでも切っておくと、万一 SQL 側が緩んでも UI が溢れない)
export const INVITE_PREVIEW_MAX_NOTEBOOKS = 10
export const INVITE_PREVIEW_MAX_SAMPLE_TASKS = 5
export const INVITE_PREVIEW_TITLE_MAX_CHARS = 60

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function toNonNegativeInt(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null
  const n = Math.floor(value)
  return n < 0 ? 0 : n
}

function parseNotebook(raw: unknown): InvitePreviewNotebook | null {
  if (!isRecord(raw)) return null
  const name = typeof raw.name === "string" ? raw.name.trim() : ""
  if (!name) return null
  const openTaskCount = toNonNegativeInt(raw.openTaskCount) ?? 0

  const sampleTasks: string[] = []
  if (Array.isArray(raw.sampleTasks)) {
    for (const item of raw.sampleTasks) {
      if (typeof item !== "string") continue
      const title = item.trim().slice(0, INVITE_PREVIEW_TITLE_MAX_CHARS)
      if (!title) continue
      sampleTasks.push(title)
      if (sampleTasks.length >= INVITE_PREVIEW_MAX_SAMPLE_TASKS) break
    }
  }

  return { name, openTaskCount, sampleTasks }
}

export function parseInvitePreview(raw: unknown): InvitePreview | null {
  if (!isRecord(raw)) return null
  const workspaceName = typeof raw.workspaceName === "string" ? raw.workspaceName.trim() : ""
  if (!workspaceName) return null
  const memberCount = toNonNegativeInt(raw.memberCount) ?? 0

  const notebooks: InvitePreviewNotebook[] = []
  if (Array.isArray(raw.notebooks)) {
    for (const item of raw.notebooks) {
      const nb = parseNotebook(item)
      if (!nb) continue
      notebooks.push(nb)
      if (notebooks.length >= INVITE_PREVIEW_MAX_NOTEBOOKS) break
    }
  }

  return { workspaceName, memberCount, notebooks }
}

export function formatOpenTaskCount(n: number): string {
  const count = toNonNegativeInt(n) ?? 0
  return `未完了 ${count} 件`
}

export function formatMemberCount(n: number): string {
  const count = toNonNegativeInt(n) ?? 0
  return `メンバー ${count} 人`
}
